import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Typography, InputNumber } from "antd";

import {
    CustomizedValidation,
    EditFormCreate,
    FormComponentProps,
    ValidationRule,
    validationRulesConverter,
} from "utils/form";

import { BaseFormDisplay } from "./formDisplay";

import { BaseFormField, EditInput, EditProps, LiteFormField } from ".";
const { Text } = Typography;

interface CreateNumericInputProps<ID extends string, R>
    extends FormComponentProps<ID, number, R>, NumericInputCommonProps {
    id: ID;
    initialValue: number | undefined;
    validators?: ValidationRule<number | undefined>[];
    empty?: boolean;
    validationError?: CustomizedValidation;
}

interface CreateNumericInputState {
    editing: boolean;
}

class CreateNumericInput<ID extends string, R> extends React.PureComponent<
    CreateNumericInputProps<ID, R>,
    CreateNumericInputState
> {
    private innerField = React.createRef<HTMLInputElement>();

    state: CreateNumericInputState = {
        editing: false,
    };

    render = () => {
        const {
            form: { isFieldValidating, getFieldError },
            id,
            label,
            validationError,
            lite,
        } = this.props;
        const { editing } = this.state;
        const readonly = !(
            editing ||
            isFieldValidating(id) ||
            getFieldError(id)
        );

        const BaseFormFieldVariant:
            | typeof LiteFormField
            | typeof BaseFormField = lite ? LiteFormField : BaseFormField;

        return (
            <BaseFormFieldVariant
                getComponent={this.getComponent}
                getReadonlyComponent={this.getReadonlyComponent}
                onClick={this.onLabelClick}
                hasFeedback={false}
                readonly={readonly}
                label={label}
                validationError={validationError}
            />
        );
    };

    getComponent = () => {
        const { form, id, validators, label, parser, formatter } = this.props;
        const { getFieldDecorator } = form;
        return getFieldDecorator(id, {
            rules: validationRulesConverter<number>()(
                id,
                form,
                validators,
                label
            ),
            valuePropName: "defaultValue",
            trigger: "onBlur",
            validateFirst: true,
            getValueFromEvent: (event: React.ChangeEvent<HTMLInputElement>) => {
                const { parser } = this.props;
                const parsedVal: string | number =
                    parser !== undefined
                        ? parser(event.target.value)
                        : event.target.value;
                if (
                    typeof parsedVal === "string" &&
                    parsedVal.trim().length === 0
                ) {
                    return undefined;
                }
                const val = Number(parsedVal);
                return isNaN(val) ? undefined : val;
            },
            preserve: true,
        })(
            <InputNumber
                ref={this.innerField}
                size="large"
                onPressEnter={this.blur}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                parser={parser}
                formatter={formatter}
            />
        );
    };

    getReadonlyComponent = () => {
        const {
            form: { getFieldValue },
            id,
            formatter,
        } = this.props;
        const value: number | undefined = getFieldValue(id);
        const empty = value === undefined;
        let text: string | number;
        if (value === undefined) {
            // TODO as unknown assertion to be removed in i18next upgrade story
            text = t("Form_No_Field_Set");
        } else if (formatter !== undefined) {
            text = formatter(value);
        } else {
            text = value;
        }
        return <Text type={empty ? "secondary" : undefined}>{text}</Text>;
    };

    onLabelClick = () => {
        if (!this.state.editing) {
            this.setState({ editing: true }, this.giveFocus);
        }
    };

    onFocus = () => {
        if (!this.state.editing) {
            this.setState({ editing: true });
        }
    };

    onBlur = () => {
        if (this.state.editing) {
            this.setState({ editing: false });
        }
    };

    blur = () => {
        if (this.innerField.current) {
            this.innerField.current.blur();
        }
    };

    giveFocus = () => {
        if (this.innerField.current) {
            this.innerField.current.focus();
        }
    };
}

const EditNumericInputForm = EditFormCreate<number>()(CreateNumericInput);

const EditNumericInput = EditInput<number>(EditNumericInputForm);

interface NumericInputCommonProps {
    label: string;
    lite?: boolean;
    parser?: (displayValue: string | undefined) => number | string;
    formatter?: (value: number | string | undefined) => string;
}

type NumericInputProps<ID extends string, R> =
    | ({ type: "READ_ONLY" } & NumericInputCommonProps & {
              value: number | undefined;
          })
    | ({ type: "EDIT" } & EditProps<number | undefined> &
          NumericInputCommonProps)
    | ({ type: "CREATE" } & CreateNumericInputProps<ID, R>);

class NumericInput<ID extends string, R> extends React.PureComponent<
    NumericInputProps<ID, R>
> {
    render = () => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const { label, value } = this.props;
                return (
                    <BaseFormDisplay label={label}>
                        <Text
                            type={value === undefined ? "secondary" : undefined}
                        >
                            {value === undefined
                                ? t("Form_No_Field_Set")
                                : value}
                        </Text>
                    </BaseFormDisplay>
                );
            }
            case "EDIT": {
                const { value, type: _type, ...rest } = this.props;
                return <EditNumericInput {...rest} value={value} />;
            }
            case "CREATE": {
                const { type: _type, ...rest } = this.props;
                return <CreateNumericInput {...rest} />;
            }
            default:
                return assertNever(this.props);
        }
    };
}
export type {
    NumericInputProps,
    NumericInputCommonProps,
    CreateNumericInputProps,
};
export { NumericInput };
